import { observable, action, computed, makeObservable } from 'mobx';

// Saved selections are only a convenience: unreadable storage or an unexpected value means "all regions"
const getStoredFilterValue = (key) => {
  try {
    const value = JSON.parse(localStorage.getItem(key));
    return Array.isArray(value) && value.every((code) => typeof code === 'string') ? value : [];
  } catch (error) {
    return [];
  }
};

const saveFilterValue = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Storage is full or blocked; the selection still applies until the page is left
  }
};

// A page's saved filters (for now, its regions), kept per page and user in localStorage under
// '<page name>_<user id>' (e.g. 'dashboard_423') so they are restored every time the page opens. Each page gets its
// own instance and calls init with its page name.
class FilterStore {
  pageName = null;

  selectedRegionCodes = [];

  constructor(regionStore, userStore) {
    this.regionStore = regionStore;
    this.userStore = userStore;

    makeObservable(this, {
      pageName: observable, // null until init: lets pages wait for the saved selection before rendering by it
      selectedRegionCodes: observable,
      init: action,
      setSelectedRegions: action,
      allRegions: computed,
      selectedRegions: computed,
      visibleRegions: computed,
    });
  }

  get storageKey() {
    return `${this.pageName}_${this.userStore.user.id}`;
  }

  get allRegions() {
    return this.regionStore.regions;
  }

  // Options for the region filter, in region order; saved codes of regions that no longer exist are skipped
  get selectedRegions() {
    return this.allRegions.filter((region) => this.selectedRegionCodes.includes(region.value));
  }

  // No selection means all regions, as in the Dispatch filter
  get visibleRegions() {
    return this.selectedRegions.length > 0 ? this.selectedRegions : this.allRegions;
  }

  // False until init has restored this page's saved filter. Lets a page wait before rendering by the selection, so it
  // never briefly shows (and loads data for) everything that is about to be filtered out.
  get initialized() {
    return this.pageName !== null;
  }

  // Regions are only fetched once per session by regionStore; the saved selection is re-read every time
  // Returns the regions request: wait for it before reading selectedRegions, which only keeps regions that exist
  init(pageName) {
    this.pageName = pageName;
    this.selectedRegionCodes = getStoredFilterValue(this.storageKey);
    return this.regionStore.init();
  }

  setSelectedRegions(regions) {
    this.selectedRegionCodes = regions.map((region) => region.value);
    saveFilterValue(this.storageKey, this.selectedRegionCodes);
  }
}

export default FilterStore;
